import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Button, IconButton, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Alert, useTheme, useMediaQuery, Chip, Card, CardContent, Skeleton, Tooltip
} from '@mui/material';
import { Add as AddIcon, Edit, Delete, Analytics as AnalyticsIcon } from '@mui/icons-material';
import SellIcon from '@mui/icons-material/Sell';
import InfoIcon from '@mui/icons-material/Info';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { getStocks, createStock, updateStock, deleteStock } from '../../services/stocksApi';
import { StockDto, InvestmentType } from '../../types/stocks';
import { batchAnalyze, PositionAnalysis } from './analysis/stockAnalysis';
import { formatEuro, formatPercent, formatQuantity, formatCryptoQuantity, formatNumber } from '../../utils/currency';
import { investmentTypeToLabel } from '../../utils/investmentType';
import { useNavigate } from 'react-router-dom';
import StockForm, { StockFormValues } from './StockForm';
import StockDetailsDialog from './StockDetailsDialog';
import AddPurchaseDialog from './AddPurchaseDialog';
import StockAnalyticsDialog from './StockAnalyticsDialog';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';

type Stock = StockDto;

export default function StocksListPage() {
  // Raw stocks array no longer needed independently; analysis keeps original reference
  interface AnalyzedStock extends PositionAnalysis { original: StockDto }
  const [analysis, setAnalysis] = useState<AnalyzedStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editStock, setEditStock] = useState<Stock | null>(null);
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stockToDelete, setStockToDelete] = useState<Stock | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsStockId, setDetailsStockId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseTargetId, setPurchaseTargetId] = useState<string | null>(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<AnalyzedStock | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Stable wrapper for attaching original stock reference to analysis (prevents load effect dependency churn)
  const wrapWithOriginal = useCallback((stock: StockDto, a: PositionAnalysis): AnalyzedStock => ({
    ...a,
    original: stock
  }), []);

  const handleAuthError = useCallback((err: any) => {
    if (err.response && (err.response.status === 401 || err.response.status === 403)) {
      setError('Sessão expirada ou sem autorização. Inicie sessão novamente.');
      navigate('/login');
      return true;
    }
    return false;
  }, [navigate]);

  const loadStocks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const all = await getStocks();
      // Show all investment types (Stocks, ETFs, Crypto, etc.)
      const computed = batchAnalyze(all).map((a, idx) => wrapWithOriginal(all[idx], a));
      setAnalysis(computed);
    } catch (err: any) {
      if (!handleAuthError(err)) {
        setError(err.message || 'Não foi possível carregar os investimentos');
      }
    } finally {
      setLoading(false);
    }
  }, [wrapWithOriginal, handleAuthError]);

  useEffect(() => {
    loadStocks();
  }, [loadStocks]);

  useEffect(() => {
    if (error) setSnackbarOpen(true);
  }, [error]);

  const handleCreate = () => {
    setEditStock(null);
    setFormOpen(true);
  };

  const handleEdit = (stock: Stock) => {
    setEditStock(stock);
    setFormOpen(true);
  };

  const handleFormSubmit = async (values: StockFormValues) => {
    try {
      if (editStock) {
        await updateStock(editStock.publicId, {
          symbol: values.symbol,
            currentPrice: values.currentPrice === '' ? undefined : values.currentPrice,
            currentCambio: values.currentCambio === '' ? undefined : values.currentCambio,
            investmentType: values.investmentType,
            currency: values.currency
          });
      } else {
        await createStock({
          symbol: values.symbol,
          investmentType: values.investmentType ?? InvestmentType.Stock,
          currentPrice: values.currentPrice === '' ? undefined : values.currentPrice,
          currentCambio: values.currentCambio === '' ? undefined : values.currentCambio,
          currency: values.currency
        });
      }
    setFormOpen(false);
    setEditStock(null);
    loadStocks();
    } catch (err: any) {
      if (!handleAuthError(err)) {
        setError(err.message || 'Não foi possível guardar o investimento');
      }
    }
  };

  const handleDeleteClick = (stock: Stock) => {
    setStockToDelete(stock);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!stockToDelete) return;
    try {
      await deleteStock(stockToDelete.publicId);
    setDeleteDialogOpen(false);
    setStockToDelete(null);
    loadStocks();
    } catch (err: any) {
      if (!handleAuthError(err)) {
        setError(err.message || 'Não foi possível eliminar o investimento');
      }
      setDeleteDialogOpen(false);
      setStockToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setStockToDelete(null);
  };


  const handleSell = (stock: AnalyzedStock) => {
    navigate(`/stocks/sales?stock=${stock.symbol}`);
  };

  const handleBuy = (stock: AnalyzedStock) => {
    setPurchaseTargetId(stock.stockPublicId);
    setPurchaseDialogOpen(true);
  };

  const openDetails = (stock: AnalyzedStock) => {
    setDetailsStockId(stock.stockPublicId);
    setDetailsOpen(true);
  };

  const openAnalytics = (stock: AnalyzedStock) => {
    setAnalyticsData(stock);
    setAnalyticsOpen(true);
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // handleAuthError defined above for dependency ordering

  const filtered = analysis.filter(a => a.symbol.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.symbol.localeCompare(b.symbol));

  // Use EUR-based totals for portfolio summary
  const totalInvested = analysis.reduce((sum, a) => sum + a.totalInvestedEur, 0);
  const totalCurrentValue = analysis.reduce((sum, a) => sum + a.currentValueEur, 0);
  const totalGainLoss = totalCurrentValue - totalInvested;
  const totalGainLossPct = totalInvested > 0 ? (totalGainLoss / totalInvested) * 100 : 0;

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Investimentos"
        subtitle="Todas as posições de investimento com métricas calculadas em tempo real"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={{ alignSelf: isMobile ? 'stretch' : 'center', minWidth: 160 }} fullWidth={isMobile}>Adicionar investimento</Button>
        }
      />

      {/* Summary Cards */}
      <AutoGrid min={150} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Total de posições</Typography>
            {loading ? <Skeleton variant="text" width={50} /> : <Typography variant="h5">{analysis.length}</Typography>}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Total investido</Typography>
            {loading ? <Skeleton variant="text" width={100} /> : <Typography variant="h6">{formatEuro(totalInvested)}</Typography>}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Valor atual</Typography>
            {loading ? <Skeleton variant="text" width={100} /> : <Typography variant="h6">{formatEuro(totalCurrentValue)}</Typography>}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Lucro/Prejuízo total</Typography>
            {loading ? <Skeleton variant="text" width={120} /> : (
              <Box display="flex" alignItems="baseline" gap={1}>
                {totalGainLoss >= 0 ? <TrendingUpIcon color="success" fontSize="small" /> : <TrendingDownIcon color="error" fontSize="small" />}
                <Typography variant="h6" color={totalGainLoss >= 0 ? 'success.main' : 'error.main'}>{formatEuro(totalGainLoss)}</Typography>
                <Typography variant="caption" color={totalGainLoss >= 0 ? 'success.main' : 'error.main'}>{formatPercent(totalGainLossPct)}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </AutoGrid>

      {/* Search */}
      <Box mb={2}>
        <TextField label="Pesquisar investimentos" value={search} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} fullWidth size={isMobile ? 'small' : 'medium'} />
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <Paper>
          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Símbolo</TableCell>
                  <TableCell>Moeda</TableCell>
                  <TableCell align="right">Unidades</TableCell>
                  <TableCell align="right">Preço</TableCell>
                  <TableCell align="right">Preço médio (EUR)</TableCell>
                  <TableCell align="right">Investido (EUR)</TableCell>
                  <TableCell align="right">Valor atual (EUR)</TableCell>
                  <TableCell align="right">Lucro/Prejuízo (sem div.)</TableCell>
                  <TableCell align="right">Lucro/Prejuízo (com div.)</TableCell>
                  <TableCell align="right">Dividendos (EUR)</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Box sx={{ py: 4 }}>
                        <Typography variant="h6" color="text.secondary">Nenhum investimento encontrado</Typography>
                        <Typography variant="body2" color="text.secondary">Adicione o seu primeiro investimento para acompanhar o desempenho</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filtered.map(stock => (
                  <TableRow key={stock.symbol} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" fontWeight="bold">{stock.symbol}</Typography>
                        <Chip size="small" label={investmentTypeToLabel(stock.original.investmentType, stock.symbol)} />
                      </Box>
                    </TableCell>
                    <TableCell>{stock.original.currency}</TableCell>
                    <TableCell align="right">{stock.original.investmentType === InvestmentType.Cryptocurrency ? formatCryptoQuantity(stock.totalShares) : formatQuantity(stock.totalShares)}</TableCell>
                    <TableCell align="right">
                      {stock.original.currentPrice != null ? (
                        <Box display="flex" flexDirection="column" alignItems="flex-end">
                          <Typography variant="body2" fontWeight="bold">
                            {formatEuro((stock.original.currentPrice ?? 0) * (stock.original.currentCambio ?? 1))}
                          </Typography>
                          {String(stock.original.currency).toUpperCase() !== 'EUR' && (
                            <Typography variant="caption" color="text.secondary">
                              {getCurrencySymbol(stock.original.currency)} {formatCoinDecimals(Number(stock.original.currentPrice ?? 0), String(stock.original.currency), stock.original.investmentType === InvestmentType.Cryptocurrency)}
                            </Typography>
                          )}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell align="right">{formatEuro(stock.averagePriceEur)}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" flexDirection="column" alignItems="flex-end">
                        <Typography variant="body2" fontWeight="bold">{formatEuro(stock.totalInvestedEur)}</Typography>
                        {String(stock.original.currency).toUpperCase() !== 'EUR' && (
                          <Typography variant="caption" color="text.secondary">
                            {getCurrencySymbol(stock.original.currency)} {formatCoinDecimals(stock.totalInvested, String(stock.original.currency), stock.original.investmentType === InvestmentType.Cryptocurrency)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" flexDirection="column" alignItems="flex-end">
                        <Typography variant="body2" fontWeight="bold">{formatEuro(stock.currentValueEur)}</Typography>
                        {String(stock.original.currency).toUpperCase() !== 'EUR' && (
                          <Typography variant="caption" color="text.secondary">
                            {getCurrencySymbol(stock.original.currency)} {formatCoinDecimals(stock.currentValue, String(stock.original.currency), stock.original.investmentType === InvestmentType.Cryptocurrency)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end">
                        {stock.gainLossNoDivEur >= 0 ? <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} /> : <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />}
                        <Box>
                          <Typography variant="body2" color={stock.gainLossNoDivEur >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">{formatEuro(stock.gainLossNoDivEur)}</Typography>
                          <Typography variant="caption" color={stock.gainLossNoDivEur >= 0 ? 'success.main' : 'error.main'}>{formatPercent(stock.gainLossNoDivEurPct)}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end">
                        {stock.gainLossWithDivEur >= 0 ? <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} /> : <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />}
                        <Box>
                          <Typography variant="body2" color={stock.gainLossWithDivEur >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">{formatEuro(stock.gainLossWithDivEur)}</Typography>
                          <Typography variant="caption" color={stock.gainLossWithDivEur >= 0 ? 'success.main' : 'error.main'}>{formatPercent(stock.gainLossWithDivEurPct)}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{formatEuro(stock.dividends)}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end">
                        <Tooltip title="Detalhes"><IconButton size="small" onClick={() => openDetails(stock)}><InfoIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(stock.original)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Análise"><IconButton size="small" onClick={() => openAnalytics(stock)}><AnalyticsIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Comprar / Adicionar compra"><IconButton size="small" color="primary" onClick={() => handleBuy(stock)}><AddShoppingCartIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Vender"><IconButton size="small" color="warning" onClick={() => handleSell(stock)}><SellIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleDeleteClick(stock.original)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <StockForm
        open={formOpen}
        initialValues={editStock ? {
          symbol: editStock.symbol ?? '',
          investmentType: editStock.investmentType,
          currentPrice: editStock.currentPrice !== undefined && editStock.currentPrice !== null ? editStock.currentPrice : '',
          currentCambio: editStock.currentCambio !== undefined && editStock.currentCambio !== null ? editStock.currentCambio : '',
          currency: editStock.currency
        } : undefined}
        onClose={() => { setFormOpen(false); setEditStock(null); }}
        onSubmit={handleFormSubmit}
      />
      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Eliminar investimento</DialogTitle>
        <DialogContent>
          <Typography>Tem a certeza de que quer eliminar o investimento <b>{stockToDelete?.symbol}</b>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Eliminar</Button>
        </DialogActions>
      </Dialog>
      <AddPurchaseDialog
        open={purchaseDialogOpen}
        stockPublicId={purchaseTargetId}
        onClose={() => setPurchaseDialogOpen(false)}
        onCreated={() => { setPurchaseDialogOpen(false); loadStocks(); }}
      />
      <StockDetailsDialog open={detailsOpen} stockId={detailsStockId} onClose={() => setDetailsOpen(false)} />
      <StockAnalyticsDialog 
        open={analyticsOpen} 
        onClose={() => setAnalyticsOpen(false)} 
        data={analyticsData} 
        isCrypto={analyticsData?.original.investmentType === InvestmentType.Cryptocurrency}
        portfolioTotals={{ totalInvested, totalValue: totalCurrentValue }}
      />
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Minimal currency symbol resolver used locally
function getCurrencySymbol(code?: string | null): string {
  const c = String(code || '').toUpperCase();
  switch (c) {
    case 'USD': return '$';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'CHF': return 'CHF';
    case 'CAD': return 'C$';
    case 'AUD': return 'A$';
    case 'BRL': return 'R$';
    case 'CNY': return '¥';
    case 'INR': return '₹';
    case 'EUR': return '€';
    default: return c || '';
  }
}

function formatCoinDecimals(amount: number, code?: string | null, isCrypto?: boolean): string {
  const c = String(code || '').toUpperCase();
  if (isCrypto) {
    // For crypto, allow up to 9 decimals and trim trailing zeros
    return formatCryptoQuantity(amount);
  }
  if (c === 'USD' || c === 'EUR') return formatNumber(amount, 2);
  return formatNumber(amount, 4);
}
